/**
 * 3D Room Visualizer — Board Layout Engine
 *
 * Pure functions that convert wall/ceiling configuration into positioned
 * board rectangles for 3D rendering. Mirrors the calculation logic in
 * src/calculations/ to produce spatially accurate board placements.
 */

import { BoardRect, WallSectionLayout, WallBoardLayout, CeilingBoardLayout, RoomBoardLayouts } from './types';
import { getProductColorHex } from './materialFactory';
import { getOptimalPanels } from '../../../data/lengthTable';
import { findOptimalPanelLength } from '../../../calculations/panels';
import {
  PANEL_WIDTH_FEET,
  SLATWALL_PANEL_LENGTH,
  SLATWALL_COVERAGE_HEIGHT,
  SLATWALL_TRANSITION_SPACE_FEET,
  calculatePanelStripsForWidth,
} from '../../../calculations/constants';
import { LengthTableEntry } from '../../../types';

// No gap between boards — seam lines are drawn as overlays instead
const BOARD_GAP = 0;

// ============================================================================
// HORIZONTAL W&CB SECTION
// ============================================================================

/**
 * Generate board rectangles for a horizontal W&CB section.
 * Panels run horizontally (16" wide rows, panel-length long).
 */
export function generateHorizontalWCBSection(
  wallLength: number,
  sectionHeight: number,
  sectionYStart: number,
  color: 'White' | 'Gray'
): BoardRect[] {
  const boards: BoardRect[] = [];
  if (wallLength <= 0 || sectionHeight <= 0) return boards;

  const colorHex = getProductColorHex(color);
  const numRows = calculatePanelStripsForWidth(sectionHeight);

  // Get optimal panel combination for this wall length
  const lengthEntry = getOptimalPanels(Math.ceil(wallLength));

  // Build ordered list of panel lengths for one row (left to right)
  const panelLengthsInRow = buildPanelLengthSequence(lengthEntry);

  for (let row = 0; row < numRows; row++) {
    const y = sectionYStart + row * PANEL_WIDTH_FEET;
    let boardHeight = PANEL_WIDTH_FEET;

    // Last row may be cut
    const rowTop = y + boardHeight;
    const sectionTop = sectionYStart + sectionHeight;
    const isTopRowCut = rowTop > sectionTop + 0.001;
    if (isTopRowCut) {
      boardHeight = sectionTop - y;
      if (boardHeight <= 0) continue;
    }

    let x = 0;
    for (let p = 0; p < panelLengthsInRow.length; p++) {
      const panelLen = panelLengthsInRow[p];
      let boardWidth = panelLen;
      let isCut = false;
      let cutEdge: BoardRect['cutEdge'] = undefined;

      // Last panel may overshoot wall length — cut it
      if (x + boardWidth > wallLength + 0.001) {
        boardWidth = wallLength - x;
        if (boardWidth <= 0) break;
        isCut = true;
        cutEdge = 'right';
      }

      // Top row cut
      if (isTopRowCut && !isCut) {
        isCut = true;
        cutEdge = 'top';
      }

      boards.push({
        x: x + BOARD_GAP / 2,
        y: y + BOARD_GAP / 2,
        width: boardWidth - BOARD_GAP,
        height: boardHeight - BOARD_GAP,
        color: colorHex,
        productType: 'WCB',
        isCut,
        cutEdge,
      });

      x += panelLen;
    }
  }

  return boards;
}

// ============================================================================
// VERTICAL W&CB SECTION
// ============================================================================

/**
 * Generate board rectangles for a vertical W&CB section.
 * Panels run vertically (16" wide strips, panel-length tall).
 */
export function generateVerticalWCBSection(
  wallLength: number,
  sectionHeight: number,
  sectionYStart: number,
  color: 'White' | 'Gray'
): BoardRect[] {
  const boards: BoardRect[] = [];
  if (wallLength <= 0 || sectionHeight <= 0) return boards;

  const colorHex = getProductColorHex(color);
  const numStrips = calculatePanelStripsForWidth(wallLength);

  // Find optimal panel length for this section height
  const [optimalPanelLength] = findOptimalPanelLength(sectionHeight, numStrips);
  const panelLength = optimalPanelLength || 8;

  for (let strip = 0; strip < numStrips; strip++) {
    const x = strip * PANEL_WIDTH_FEET;
    let boardWidth = PANEL_WIDTH_FEET;
    let isCut = false;
    let cutEdge: BoardRect['cutEdge'] = undefined;

    // Last strip may be cut
    if (x + boardWidth > wallLength + 0.001) {
      boardWidth = wallLength - x;
      if (boardWidth <= 0) continue;
      isCut = true;
      cutEdge = 'right';
    }

    let boardHeight = panelLength;
    // Panel may be taller than section — cut at top
    if (boardHeight > sectionHeight + 0.001) {
      boardHeight = sectionHeight;
      if (!isCut) {
        isCut = true;
        cutEdge = 'top';
      }
    }

    boards.push({
      x: x + BOARD_GAP / 2,
      y: sectionYStart + BOARD_GAP / 2,
      width: boardWidth - BOARD_GAP,
      height: boardHeight - BOARD_GAP,
      color: colorHex,
      productType: 'WCB',
      isCut,
      cutEdge,
    });
  }

  return boards;
}

// ============================================================================
// SLATWALL SECTION
// ============================================================================

/**
 * Generate board rectangles for a SlatWall section.
 *
 * SlatWall panels interlock seamlessly — no visible gaps between panels.
 * Rendered as a single solid rectangle covering the full section.
 * Groove lines are added separately by the mesh builder.
 */
export function generateSlatWallSection(
  wallLength: number,
  numPanelRows: number,
  sectionYStart: number,
  color: 'White' | 'Gray'
): BoardRect[] {
  const boards: BoardRect[] = [];
  if (wallLength <= 0 || numPanelRows <= 0) return boards;

  const colorHex = getProductColorHex(color);
  const totalHeight = numPanelRows * SLATWALL_COVERAGE_HEIGHT;

  // Single solid rectangle for the entire SlatWall section
  boards.push({
    x: 0,
    y: sectionYStart,
    width: wallLength,
    height: totalHeight,
    color: colorHex,
    productType: 'SlatWall',
    isCut: false,
  });

  return boards;
}

// ============================================================================
// WALL LAYOUT ORCHESTRATION
// ============================================================================

/**
 * Generate complete board layout for a single wall.
 */
export function generateWallBoardLayout(
  wallId: string,
  wallLength: number,
  wallHeight: number,
  wallColor: 'White' | 'Gray',
  orientation: 'Horizontal' | 'Vertical',
  slatWallConfig?: {
    enabled: boolean;
    startHeight: number;
    numPanelRows: number;
    color: 'White' | 'Gray';
  } | null
): WallBoardLayout {
  const sections: WallSectionLayout[] = [];

  if (slatWallConfig?.enabled && slatWallConfig.numPanelRows > 0) {
    // Three sections: below SW, SlatWall, above SW
    // For visual rendering, skip the 1" trim transition space — W&CB fills
    // all remaining wall height above and below SlatWall with no gaps.
    const belowHeight = slatWallConfig.startHeight;
    const swHeight = slatWallConfig.numPanelRows * SLATWALL_COVERAGE_HEIGHT;
    const swYStart = belowHeight;
    const aboveYStart = belowHeight + swHeight;
    const aboveHeight = Math.max(0, wallHeight - aboveYStart);

    // Below SlatWall (W&CB)
    if (belowHeight > 0) {
      const generateSection = orientation === 'Horizontal'
        ? generateHorizontalWCBSection
        : generateVerticalWCBSection;

      sections.push({
        sectionType: 'BelowSlatWall',
        yStart: 0,
        height: belowHeight,
        boards: generateSection(wallLength, belowHeight, 0, wallColor),
      });
    }

    // SlatWall
    sections.push({
      sectionType: 'SlatWall',
      yStart: swYStart,
      height: swHeight,
      boards: generateSlatWallSection(
        wallLength,
        slatWallConfig.numPanelRows,
        swYStart,
        slatWallConfig.color
      ),
    });

    // Above SlatWall (W&CB)
    if (aboveHeight > 0) {
      const generateSection = orientation === 'Horizontal'
        ? generateHorizontalWCBSection
        : generateVerticalWCBSection;

      sections.push({
        sectionType: 'AboveSlatWall',
        yStart: aboveYStart,
        height: aboveHeight,
        boards: generateSection(wallLength, aboveHeight, aboveYStart, wallColor),
      });
    }
  } else {
    // Full wall — single W&CB section
    const generateSection = orientation === 'Horizontal'
      ? generateHorizontalWCBSection
      : generateVerticalWCBSection;

    sections.push({
      sectionType: 'FullWall',
      yStart: 0,
      height: wallHeight,
      boards: generateSection(wallLength, wallHeight, 0, wallColor),
    });
  }

  return {
    wallId,
    wallLength,
    wallHeight,
    sections,
  };
}

/**
 * Generate board layout for a ceiling surface.
 * Ceilings always use horizontal orientation.
 */
export function generateCeilingBoardLayout(
  ceilingLength: number,
  ceilingWidth: number,
  color: 'White' | 'Gray'
): CeilingBoardLayout {
  // Ceiling boards run along the length, rows stack along the width
  const boards = generateHorizontalWCBSection(ceilingLength, ceilingWidth, 0, color);

  return {
    length: ceilingLength,
    width: ceilingWidth,
    boards,
  };
}

/**
 * Generate all board layouts for a full room (4 walls + optional ceiling).
 * Wall order: [front (length), right (width), back (length), left (width)]
 */
export function generateFullRoomLayouts(
  roomLength: number,
  roomWidth: number,
  roomHeight: number,
  wallColor: 'White' | 'Gray',
  wallOrientation: 'Horizontal' | 'Vertical',
  slatWallConfig?: {
    enabled: boolean;
    startHeight: number;
    numPanelRows: number;
    color: 'White' | 'Gray';
  } | null,
  ceilingColor?: 'White' | 'Gray'
): RoomBoardLayouts {
  // Front wall (along room length, at z=0)
  const front = generateWallBoardLayout(
    'front', roomLength, roomHeight, wallColor, wallOrientation, slatWallConfig
  );

  // Right wall (along room width, at x=length)
  const right = generateWallBoardLayout(
    'right', roomWidth, roomHeight, wallColor, wallOrientation, slatWallConfig
  );

  // Back wall (along room length, at z=width)
  const back = generateWallBoardLayout(
    'back', roomLength, roomHeight, wallColor, wallOrientation, slatWallConfig
  );

  // Left wall (along room width, at x=0)
  const left = generateWallBoardLayout(
    'left', roomWidth, roomHeight, wallColor, wallOrientation, slatWallConfig
  );

  const result: RoomBoardLayouts = {
    walls: [front, right, back, left],
  };

  if (ceilingColor) {
    result.ceiling = generateCeilingBoardLayout(roomLength, roomWidth, ceilingColor);
  }

  return result;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Build an ordered sequence of panel lengths for one horizontal row.
 * Converts the LengthTableEntry counts into an ordered left-to-right sequence.
 */
function buildPanelLengthSequence(entry: LengthTableEntry): number[] {
  const sequence: number[] = [];
  const lengths = [20, 16, 14, 12, 10, 8] as const;

  for (const len of lengths) {
    const count = entry[String(len) as keyof LengthTableEntry] as number;
    for (let i = 0; i < count; i++) {
      sequence.push(len);
    }
  }

  return sequence;
}
